import { useEffect, useState } from "react";
import type { GroupBase, OptionsOrGroups } from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { apiRequest } from "shared/api/apiRequest";
import { getSocietyProfileEducationInstanceContract } from "shared/api/contracts/society/societyId/profiles/profileId/education/instance";
import { type EducationInstanceView } from "shared/mappers/database/education/education-instance";
import { type SessionView } from "shared/mappers/database/session/session";

export type EducationInstanceQuestionComponentProps = {
  session: SessionView | null;
  baseUrl?: string;
  handleChanged: ({
    educationInstance,
    hoursToUse,
  }: {
    educationInstance: EducationInstanceView | null;
    hoursToUse: number | null;
  }) => void;
  existingValue?: {
    educationInstance: EducationInstanceView;
    hoursToUse: number;
  };
  existingHoursToUse?: number;
  hoursRequired?: boolean;
  completedAfter?: string;
  placeHolderText?: string;
};
export type OptionsValuesEducationInstanceQuestionComponent = {
  value: EducationInstanceView;
  label: string;
} | null;

const EducationInstanceQuestionComponent = ({
  session,
  handleChanged,
  existingValue,
  hoursRequired,
  completedAfter,
  placeHolderText,
}: EducationInstanceQuestionComponentProps) => {
  const [newQuestionValue, setNewQuestionValue] =
    useState<OptionsValuesEducationInstanceQuestionComponent | null>(null);
  const [hoursToUse, setHoursToUse] = useState<number | null>(null);

  useEffect(() => {
    if (existingValue && !newQuestionValue) {
      const educationInstance =
        existingValue.educationInstance ?? existingValue;
      const hours = existingValue.hoursToUse ?? educationInstance.hoursEarned;

      setNewQuestionValue({
        value: educationInstance,
        label:
          educationInstance.overrideName ??
          educationInstance.educationDefinition?.educationName ??
          "",
      });

      setHoursToUse(hours);
    }
  }, [existingValue, newQuestionValue]);

  const fetchEducationInstances = async (
    inputValue: string,
    loadedOptions: OptionsOrGroups<
      OptionsValuesEducationInstanceQuestionComponent,
      GroupBase<OptionsValuesEducationInstanceQuestionComponent>
    >,
    page = 1,
  ) => {
    const pageSize = 25;
    const response = await apiRequest({
      contract: getSocietyProfileEducationInstanceContract,
      inputs: {
        params: {
          societyId: Number(session?.societyId) ?? "",
          profileId: Number(session?.profileId) ?? "",
        },
      },
      filters: { text: inputValue, ["completed-after"]: completedAfter },
      pagination: { page, pageSize },
    });

    const options = response.results.map((item) => ({
      value: {
        educationInstanceId: item.educationInstanceId,
        overrideName: item.overrideName,
      },
      label: item.overrideName ?? item.educationDefinition?.educationName ?? "",
    }));

    const hasMore = response.page < Math.ceil(response.totalResults / pageSize);

    return { options, hasMore, additional: page + 1 };
  };

  return (
    <div className="flex flex-col gap-y-2">
      <AsyncPaginate
        loadOptions={fetchEducationInstances}
        isDisabled={false}
        debounceTimeout={500}
        placeholder={placeHolderText ?? "Search for the education item."}
        value={newQuestionValue}
        onChange={(option: OptionsValuesEducationInstanceQuestionComponent) => {
          if (!option) {
            return;
          }

          setNewQuestionValue(option);
          setHoursToUse(option.value.hoursEarned ?? null);
          handleChanged({
            educationInstance: option.value,
            hoursToUse: option.value.hoursEarned ?? null,
          });
        }}
        additional={1}
      />
      {newQuestionValue && (
        <>
          <div>
            Enter how many hours to use (max:
            {newQuestionValue.value.hoursEarned})
          </div>
          <input
            type="number"
            placeholder={`Hours to use (max: ${newQuestionValue.value.hoursEarned})`}
            max={newQuestionValue.value.hoursEarned ?? 0}
            value={hoursToUse ?? undefined}
            onChange={(event) => {
              if (
                newQuestionValue.value.hoursEarned &&
                +event.target.value > +newQuestionValue.value.hoursEarned
              ) {
                event.target.value = String(newQuestionValue.value.hoursEarned);
              }
              setHoursToUse(Number(event.target.value));
              handleChanged({
                educationInstance: newQuestionValue.value,
                hoursToUse: Number(event.target.value),
              });
            }}
            className="sd-input sd-text"
            required={hoursRequired ?? true}
          />
        </>
      )}
    </div>
  );
};

export default EducationInstanceQuestionComponent;
